// What a saved Course Builder course is allowed to be used for.
//
// Two kinds of course exist and they must never stand in for one another:
//
//   A course assessment is the theory day a student sits after their EOI. It is tied to a
//   delivery program through linkedCourse, and the server serves the newest one tied to
//   that program as the live assessment for every new enrolment.
//
//   A practical assessment is the separate session a student sits after completing the
//   theory day and both declarations. A trainer marks it against outcome matrices; there
//   is no student signature and nothing for a student to submit. It is identified by
//   workflowKind and must never be tied to a delivery program.
//
// Nothing reads a course's name, so calling one "Practical Assessment" does not make it
// one. Until these rules existed, a practical pointed at Traffic Controller was, in the
// only language the system reads, a claim to be the Traffic Controller theory assessment -
// and being the most recently saved, it replaced it for everyone enrolling afterwards.
//
// functions/startTrafficImplement.js enforces the serving half of this on the server,
// which is the half that protects students. This module is the authoring half: it keeps
// Course Builder from offering the mistake in the first place. The two are deliberately
// separate copies because the site and the functions deploy independently, so neither may
// rely on the other being current. Keep isServableCourse here and isServableBuilderCourse
// there in step.

export const PRACTICAL_WORKFLOW_KIND = 'traffic-practical-v1';

export const COURSE_KINDS = Object.freeze({ course: 'course', practical: 'practical' });

export const COURSE_KIND_LABELS = Object.freeze({
  course: 'Course assessment',
  practical: 'Practical assessment',
});

// A course's kind lives in its metadata, so it travels with every save, draft and reload
// without a second field to keep in sync.
export const getCourseKind = (course = {}) => (
  course?.courseMeta?.workflowKind === PRACTICAL_WORKFLOW_KIND ? COURSE_KINDS.practical : COURSE_KINDS.course
);

export const isPracticalCourse = (course = {}) => getCourseKind(course) === COURSE_KINDS.practical;

// A Practical task holds an outcome matrix for a trainer to mark, so its presence means
// the course is a practical whatever its metadata says.
export const hasPracticalTasks = (tasks = []) => (
  Array.isArray(tasks) && tasks.some((task) => task?.type === 'Practical')
);

// Mirrors isServableBuilderCourse in functions/startTrafficImplement.js. A course is only
// fit to be served to students as a delivery program's assessment when it is finished
// enough to sit and is not a practical.
export const isServableCourse = (course = {}) => {
  const tasks = Array.isArray(course?.tasks) ? course.tasks : [];
  return tasks.length > 0
    && tasks.every((task) => String(task?.type || '').trim() !== '')
    && tasks.some((task) => task?.type === 'Trainer/Assessor Declaration')
    && !hasPracticalTasks(tasks)
    && !isPracticalCourse(course);
};

// The course each delivery program currently serves: the newest servable one, which is the
// same choice the server makes. Everything else tied to that program is dormant, and
// saying so is the point - two courses both reading "Traffic Controller" otherwise look
// equally live.
export const getLiveCourseIdsByProgram = (courses = []) => {
  const live = {};
  for (const course of Array.isArray(courses) ? courses : []) {
    const program = course?.linkedCourse;
    if (!program || !isServableCourse(course)) continue;
    const current = live[program];
    if (!current || Number(course.updatedAtMs || 0) > Number(current.updatedAtMs || 0)) live[program] = course;
  }
  return Object.fromEntries(Object.entries(live).map(([program, course]) => [program, course.id]));
};

// Why a course may not be saved against a delivery program. Returns '' when it may.
// A practical is refused outright rather than silently unlinked, because a course that
// claims to be both is a mistake the author should see and resolve.
export const validateCourseLinkage = ({ linkedCourse = '', courseMeta = {}, tasks = [] } = {}) => {
  if (!linkedCourse) return '';
  if (getCourseKind({ courseMeta }) === COURSE_KINDS.practical) {
    return 'A practical assessment cannot be linked to a delivery program. Students reach it after completing the course assessment and declarations, so clear the linked program or change the course type.';
  }
  if (hasPracticalTasks(tasks)) {
    return 'This course contains practical project tasks, so it cannot be linked to a delivery program. Set its course type to Practical assessment, or remove the practical tasks.';
  }
  return '';
};

// What saving this course will do to students, so it can be said before the write rather
// than discovered afterwards.
export const describeLinkageImpact = ({ id = '', linkedCourse = '', courseMeta = {}, tasks = [],
  savedCourses = [], programName = '' } = {}) => {
  if (!linkedCourse) return '';
  const candidate = { id, linkedCourse, courseMeta, tasks, updatedAtMs: Date.now() };
  if (!isServableCourse(candidate)) return '';
  const displaced = (savedCourses || []).find((course) => course.id !== id
    && course.linkedCourse === linkedCourse && isServableCourse(course));
  const program = programName || linkedCourse;
  return displaced
    ? `Saving this will make it the live ${program} assessment for every new enrolment, replacing "${displaced.courseName || displaced.name || 'Untitled course'}" (${(displaced.tasks || []).length} stages). Students already enrolled keep the course they started.`
    : `Saving this will make it the live ${program} assessment for every new enrolment.`;
};
